/**
 * Servicio para gestión de SIMCE del profesor
 */

import {
    ActualizarPreguntaSimceDTO,
    ActualizarPruebaSimceDTO,
    AsignacionPruebaSimce,
    AsignacionPruebaSimceDetalle,
    AsignarPruebaSimceDTO,
    AsignaturaSimce,
    CrearPreguntaSimceDTO,
    CrearPruebaSimceDTO,
    FiltrosAsignacionesPruebaSimce,
    FiltrosPreguntasSimce,
    NivelSimce,
    PreguntaSimce,
    PruebaSimce,
} from "../../app/profesor/simce/dto/simce.dto";
import { apiClientWeb } from "../../lib/apiClientWeb";

export const simceService = {
  /**
   * Obtiene preguntas SIMCE con filtros
   */
  getPreguntas: async (
    filtros: FiltrosPreguntasSimce = {}
  ): Promise<PreguntaSimce[]> => {
    const params = new URLSearchParams();

    if (filtros.asignatura) params.append("asignatura", filtros.asignatura);
    if (filtros.nivel) params.append("nivel", filtros.nivel);
    if (filtros.dificultad) params.append("dificultad", filtros.dificultad);
    if (filtros.revisada !== undefined)
      params.append("revisada", String(filtros.revisada));
    if (filtros.tipoCreador) params.append("tipoCreador", filtros.tipoCreador);
    if (filtros.soloMiColegio !== undefined)
      params.append("soloMiColegio", String(filtros.soloMiColegio));
    if (filtros.busqueda) params.append("busqueda", filtros.busqueda);
    if (filtros.vigente !== undefined)
      params.append("vigente", String(filtros.vigente));

    const response = await apiClientWeb.get(
      `/api/profesor/simce/preguntas?${params.toString()}`
    );
    return response.data.data || response.data;
  },

  /**
   * Obtiene una pregunta SIMCE por ID
   */
  getPreguntaById: async (idPreguntaSimce: number): Promise<PreguntaSimce> => {
    const response = await apiClientWeb.get(
      `/api/profesor/simce/preguntas/${idPreguntaSimce}`
    );
    return response.data.data || response.data;
  },

  /**
   * Crea una nueva pregunta SIMCE
   */
  createPregunta: async (
    data: CrearPreguntaSimceDTO
  ): Promise<PreguntaSimce> => {
    // Transformar alternativas para incluir esCorrecta
    const alternativas = data.alternativas.map((alt, index) => ({
      letra: alt.letra,
      texto: alt.texto,
      esCorrecta: alt.letra === data.respuestaCorrecta,
    }));

    const payload = {
      ...data,
      alternativas,
    };

    const response = await apiClientWeb.post(
      `/api/profesor/simce/preguntas`,
      payload
    );
    return response.data.data || response.data;
  },

  /**
   * Actualiza una pregunta SIMCE
   */
  updatePregunta: async (
    data: ActualizarPreguntaSimceDTO
  ): Promise<PreguntaSimce> => {
    const { idPreguntaSimce, ...updateData } = data;

    // Si hay alternativas y respuestaCorrecta, transformarlas
    if (updateData.alternativas && updateData.respuestaCorrecta) {
      updateData.alternativas = updateData.alternativas.map((alt) => ({
        letra: alt.letra,
        texto: alt.texto,
        esCorrecta: alt.letra === updateData.respuestaCorrecta,
      })) as any;
    }

    const response = await apiClientWeb.put(
      `/api/profesor/simce/preguntas/${idPreguntaSimce}`,
      updateData
    );
    return response.data.data || response.data;
  },

  /**
   * Elimina una pregunta SIMCE
   */
  deletePregunta: async (idPreguntaSimce: number): Promise<void> => {
    await apiClientWeb.delete(
      `/api/profesor/simce/preguntas/${idPreguntaSimce}`
    );
  },

  /**
   * Obtiene pruebas SIMCE con filtros
   */
  getPruebas: async (
    filtros: {
      asignatura?: AsignaturaSimce;
      nivel?: NivelSimce;
      busqueda?: string;
      vigente?: boolean;
    } = {}
  ): Promise<PruebaSimce[]> => {
    const params = new URLSearchParams();

    if (filtros.asignatura) params.append("asignatura", filtros.asignatura);
    if (filtros.nivel) params.append("nivel", filtros.nivel);
    if (filtros.busqueda) params.append("busqueda", filtros.busqueda);
    if (filtros.vigente !== undefined)
      params.append("vigente", String(filtros.vigente));

    const response = await apiClientWeb.get(
      `/api/profesor/simce/pruebas?${params.toString()}`
    );
    return response.data.data || response.data;
  },

  /**
   * Obtiene una prueba SIMCE por ID
   */
  getPruebaById: async (idPruebaSimce: number): Promise<PruebaSimce> => {
    const response = await apiClientWeb.get(
      `/api/profesor/simce/pruebas/${idPruebaSimce}`
    );
    return response.data.data || response.data;
  },

  /**
   * Crea una nueva prueba SIMCE
   */
  createPrueba: async (data: CrearPruebaSimceDTO): Promise<PruebaSimce> => {
    const response = await apiClientWeb.post(
      `/api/profesor/simce/pruebas`,
      data
    );
    return response.data.data || response.data;
  },

  /**
   * Actualiza una prueba SIMCE
   */
  updatePrueba: async (
    data: ActualizarPruebaSimceDTO
  ): Promise<PruebaSimce> => {
    const { idPruebaSimce, ...updateData } = data;

    const response = await apiClientWeb.put(
      `/api/profesor/simce/pruebas/${idPruebaSimce}`,
      updateData
    );
    return response.data.data || response.data;
  },

  /**
   * Elimina una prueba SIMCE
   */
  deletePrueba: async (idPruebaSimce: number): Promise<void> => {
    await apiClientWeb.delete(`/api/profesor/simce/pruebas/${idPruebaSimce}`);
  },

  /**
   * Obtiene asignaciones de pruebas SIMCE
   */
  getAsignaciones: async (
    filtros: FiltrosAsignacionesPruebaSimce = {}
  ): Promise<AsignacionPruebaSimce[]> => {
    const params = new URLSearchParams();

    if (filtros.pruebaSimceId)
      params.append("pruebaSimceId", String(filtros.pruebaSimceId));
    if (filtros.estudianteId)
      params.append("estudianteId", String(filtros.estudianteId));
    if (filtros.estado) params.append("estado", filtros.estado);

    const response = await apiClientWeb.get(
      `/api/profesor/simce/asignaciones?${params.toString()}`
    );
    return response.data.data || response.data;
  },

  /**
   * Obtiene una asignación por ID
   */
  getAsignacionById: async (
    idAsignacionPruebaSimce: number
  ): Promise<AsignacionPruebaSimceDetalle> => {
    const response = await apiClientWeb.get(
      `/api/profesor/simce/asignaciones/${idAsignacionPruebaSimce}`
    );
    return response.data.data || response.data;
  },

  /**
   * Asigna una prueba SIMCE a estudiantes
   */
  asignarPrueba: async (
    data: AsignarPruebaSimceDTO
  ): Promise<AsignacionPruebaSimce[]> => {
    const response = await apiClientWeb.post(
      `/api/profesor/simce/asignaciones`,
      data
    );
    return response.data.data || response.data;
  },

  /**
   * Actualiza el estado de una asignación
   */
  updateEstadoAsignacion: async (
    idAsignacionPruebaSimce: number,
    estado: string,
    fechaLimite?: string | null
  ): Promise<AsignacionPruebaSimce> => {
    const response = await apiClientWeb.patch(
      `/api/profesor/simce/asignaciones/${idAsignacionPruebaSimce}/estado`,
      { estado, fechaLimite }
    );
    return response.data.data || response.data;
  },

  /**
   * Elimina una asignación
   */
  deleteAsignacion: async (
    idAsignacionPruebaSimce: number
  ): Promise<void> => {
    await apiClientWeb.delete(
      `/api/profesor/simce/asignaciones/${idAsignacionPruebaSimce}`
    );
  },
};
